import { action } from '@ember/object';
import EmberService, { service } from '@ember/service';

import type {
	APIData,
	LineageNavigateIntent,
	LineageVerifyTarget,
	ReactIntegrations,
	Service,
} from 'webapp-types';
import { ActivityLogObjectType } from 'webapp/enums/activity-log';
import {
	ProductIssueType,
	ReportIssueSenderType,
	VerificationObjectType,
	VerificationStatus,
} from 'webapp/enums/enums';
import { AppEvent } from 'webapp/enums/events';
import type { VerificationInfo } from 'webapp/utils/verification';

import type InfolayerItemDataExport from 'webapp/models-custom/infolayer-item/data-export';
import type InfolayerItemDatasetSync from 'webapp/models-custom/infolayer-item/dataset-sync';
import MetricSetup from 'webapp/models-custom/metric-setup';
import type Source from 'webapp/models-custom/source';
import { SourceOption } from 'webapp/models-custom/source-type';
import { DataSourceSyncStatus, DataSourceSyncType } from 'webapp/models/source';

import type { EventIssueData } from 'webapp/services/issues-report';

export default class DatasetEventsService extends EmberService {
	@service('navigation') declare navigationService: Service.Navigation;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('account') declare accountService: Service.Account;
	@service('issues-report') declare issuesReportService: Service.IssuesReport;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;
	@service('metric-details')
	declare metricDetailsService: Service.MetricDetails;
	@service('logger') declare loggerService: Service.Logger;

	@service('model-manager/custom-queries')
	declare mmCustomQueriesService: Service.ModelManager.CustomQueries;
	@service('model-manager/blocks') declare mmBlocksService: Service.ModelManager.Blocks;
	@service('store') declare storeService: Service.Store;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;
	@service('model-manager/activity-logs')
	declare mmActivityLogsService: Service.ModelManager.ActivityLogs;
	@service('lineage') declare lineageService: Service.Lineage;

	dataExportInfoLayer: InfolayerItemDataExport | null = null;
	dataSyncInfoLayers: Record<number, InfolayerItemDatasetSync> = {};
	datasetsSchemaExplorerUrl = '';

	private logToConsole(info: string, ...params: unknown[]) {
		this.loggerService.logToConsole(`DatasetEventsService - ${info}`, ...params);
	}

	@action
	isReportableError(error?: ReactIntegrations.Error): error is ReactIntegrations.Error & {
		data: { requestId: string; status: number };
		meta: {
			url: string;
		};
	} {
		return Boolean(error?.data?.requestId && error?.meta?.url && error?.data?.status);
	}

	@action
	getErrorInfoText(error?: ReactIntegrations.Error, text?: string) {
		if (error?.data?.statusObject?.title && error.data.statusObject.description) {
			return `<b>${error.data.statusObject.title}</b><br>${error.data.statusObject.description}`;
		} else if (error?.data?.title) {
			return error.data.title;
		} else {
			return text ?? 'Oops, something went wrong!';
		}
	}

	@action
	handleErrorMessage(error?: ReactIntegrations.Error, text?: string) {
		if (error || text) {
			this.logToConsole('handleErrorMessage', error, text);
		}

		const errorInfoText = this.getErrorInfoText(error, text);

		const eventIssueData: EventIssueData = {
			type: ProductIssueType.DATASET_ERROR,
			endpoint: error?.meta?.url || '',
			page: window.location.pathname,
			url: window.location.href,
			httpStatusCode: error?.data?.status,
			errorMessage:
				error?.data?.statusObject?.title || error?.data?.title || text || 'unknown',
			requestId: error?.data?.requestId,
		};

		if (this.isReportableError(error)) {
			this.issuesReportService.showInfolayerError(errorInfoText, {
				endpoint: error.meta?.url,
				httpStatusCode: error.data?.status,
				errorText: JSON.stringify(error),
				requestId: error.data?.requestId,
				senderType: ReportIssueSenderType.DATASET_EVENTS,
				eventIssueData,
			});
		} else {
			this.infolayerService.error(errorInfoText);
		}
	}

	@action
	handleTransitionToRoute(route: string) {
		this.navigationService.transitionToRoute(route);
	}

	@action
	handleTransitionToRouteWithId(route: string, id: string) {
		this.navigationService.transitionToRoute(route, id);
	}

	@action
	handleDataRefresh(error?: ReactIntegrations.Error) {
		if (!error) {
			this.infolayerService.ok(
				'The data refetch request has been sent. Hold tight while we prepare the updated data for you!',
			);
		} else {
			this.handleErrorMessage(error);
		}
	}

	@action
	handleSourceDuplicated() {
		this.infolayerService.ok('Data source was successfully duplicated');
	}

	@action
	handleDuplicateDataSourceError(error: ReactIntegrations.Error) {
		this.handleErrorMessage(error, 'Error duplicating data source');
	}

	@action
	handleViewDataManager(sourceOption?: SourceOption) {
		switch (sourceOption) {
			case SourceOption.DATASETS:
				this.navigationService.transitionToRoute('datamanager.datasets');
				break;
			case SourceOption.MERGED_DATASETS:
				this.navigationService.transitionToRoute('datamanager.merged-datasets');
				break;
			default:
				this.navigationService.transitionToRoute('datamanager.connected');
		}
	}

	@action
	handleDeleteSourceError() {
		this.handleErrorMessage(undefined, 'Error deleting source');
	}

	@action
	handleExportError(error?: ReactIntegrations.Error) {
		this.dataExportInfoLayer?.setError(error?.data?.title ?? 'Unknown error');
	}

	@action
	handleDataExport(
		name: string,
		spaceAccessId: number,
		url?: string,
		fileSize?: string,
		type?: string,
	) {
		if (url) {
			this.dataExportInfoLayer?.setDone({
				url,
				name,
				fileSize,
				type,
				spaceAccessId,
			});
		} else {
			this.dataExportInfoLayer = this.infolayerService.dataExport({
				name,
				spaceAccessId,
			});
		}
	}

	@action
	handleValidationError(error?: ReactIntegrations.Error) {
		this.handleErrorMessage(error);
	}

	@action
	handleViewDataset(id: Source['id']) {
		this.handleTransitionToRouteWithId('datamanager.connected.datasets.view', id!);
	}

	@action
	handleViewMergedDatasets() {
		this.handleViewDataManager(SourceOption.MERGED_DATASETS);
	}

	@action
	handleEditDataset(id: Source['id']) {
		this.handleTransitionToRouteWithId('datamanager.connected.datasets.edit', id!);
	}

	@action
	handleViewDataSources() {
		this.navigationService.transitionToRoute('datamanager.connected.index');
	}

	@action
	handleDatasetDeleted() {
		this.infolayerService.ok('Dataset deleted successfully');
	}

	/**
	 * The callback a React dataset page opens its activity log through, or undefined for a user who
	 * may not read one - which is what keeps the menu item out of their menu. Only admins may.
	 */
	get openActivityLogCallback() {
		return this.currentUserService.isAdmin ? this.handleOpenActivityLog : undefined;
	}

	@action
	handleOpenActivityLog(spaceAccessId: number) {
		// the billing check lives in createAndOpenPopup
		const source = this.mmSourcesService.findModel(String(spaceAccessId));
		if (!source) {
			this.logToConsole('handleOpenActivityLog - source not found in store', spaceAccessId);
			return;
		}

		this.mmActivityLogsService.createAndOpenPopup({
			model: source,
			objectTypes: [ActivityLogObjectType.DATASET],
		});
	}

	@action
	handleMetricCreate(id: Source['id'], invalidatedGetMetricQuery: (() => void) | null) {
		this.mmCustomQueriesService.openCreateNewPopup(
			'datasets',
			() => {
				invalidatedGetMetricQuery?.();
			},
			id ? +id : undefined,
		);
	}

	@action
	handleChangeSyncInterval() {
		this.infolayerService.ok('Data sync frequency was successfully updated');
	}

	@action
	handleMetricEdit(
		_id: Source['id'],
		queryId: number,
		invalidatedGetMetricQuery: (() => void) | null,
	) {
		if (this.accountService.handleDemo()) {
			this.logToConsole('handleMetricEdit - demo user, aborting', queryId);
			return;
		}

		const afterChangesEvent = () => {
			invalidatedGetMetricQuery?.();
		};

		void this.mmCustomQueriesService.requestCustomQueries().then(() => {
			const query = this.mmCustomQueriesService.findModel(String(queryId));
			if (query) {
				this.mmCustomQueriesService.openEditPopup(
					query,
					'datasets',
					undefined,
					afterChangesEvent,
					afterChangesEvent,
				);
			} else {
				this.logToConsole('handleMetricEdit - custom query not found', queryId);
			}
		});
	}

	@action
	handleMetricDuplicate(queryId: number, invalidatedGetMetricQuery: (() => void) | null) {
		if (this.accountService.handleDemo()) {
			this.logToConsole('handleMetricDuplicate - demo user, aborting', queryId);
			return;
		}

		void this.mmCustomQueriesService.requestCustomQueries().then(() => {
			const query = this.mmCustomQueriesService.findModel(String(queryId));
			if (!query) {
				this.infolayerService.error('Non-existent built metric requested');
				return;
			}

			invalidatedGetMetricQuery?.();

			this.mmCustomQueriesService.cloneQuery(query, 'datasets');
		});
	}

	@action
	async handleAddToDataboard(metricKey: string) {
		if (this.currentUserService.handleDemo()) {
			this.logToConsole('handleAddToDataboard - demo user, aborting', metricKey);
			return;
		}

		// The metrics store is filled on request rather than eagerly per route; `requestMetrics` is
		// deduplicated, so this is a no-op once the store is warm.
		await this.mmMetricsService.requestMetrics();

		const metric = this.mmMetricsService.findRecord(metricKey);
		if (!metric) {
			this.logToConsole(
				'handleAddToDataboard - metric not found in store',
				metricKey,
				'similar record ids:',
				this.mmMetricsService.savedRecords
					.map((record) => String(record.id))
					.filter((id) => id.startsWith(metricKey)),
			);
			return;
		}

		const metricSetup = new MetricSetup(metric.metricData!);

		this.mmBlocksService.openCreateFromMetricSetupPopup({
			trackOrigin: 'datasets',
			createdNow: false,
			metricSetup,
		});
	}

	@action
	handleOnConnectionUpdated(error?: ReactIntegrations.Error) {
		if (!error) {
			this.infolayerService.ok('Connection successfully updated!');
		} else {
			this.handleErrorMessage(error);
		}
	}

	@action
	handleAddDataset() {
		this.infolayerService.ok('Dataset added successfully');
	}

	@action
	handleMergeOptionsSuccess() {
		this.infolayerService.ok('Updated successfully');
	}

	@action
	handleMergeOptionsError(error?: ReactIntegrations.Error) {
		this.handleErrorMessage(error);
	}

	@action
	handleColumnSelectionSuccess() {
		this.infolayerService.ok('Updated successfully');
	}

	@action
	handleRemoveMergeDatasetSuccess(redirect: boolean) {
		if (redirect) {
			this.handleViewMergedDatasets();
		}

		this.infolayerService.ok('Removed successfully');
	}

	@action
	handleRemoveMergeDatasetError(error?: ReactIntegrations.Error) {
		this.handleErrorMessage(error);
	}

	@action
	handleApiError(error?: ReactIntegrations.Error | string) {
		if (typeof error === 'string') {
			this.handleErrorMessage(undefined, error);
		} else {
			this.handleErrorMessage(error);
		}
	}

	@action
	handleDatasetFinishSetupSuccess() {
		this.infolayerService.ok('Dataset successfully updated!');
	}

	@action
	handleSemanticMetadataSaved(datasetName: string, columnName?: string) {
		const message = columnName
			? `Semantic metadata for column <strong>${columnName}</strong> in <strong>${datasetName}</strong> has been saved successfully.`
			: `Semantic metadata for <strong>${datasetName}</strong> has been saved successfully.`;
		this.infolayerService.ok(message);
	}

	@action
	// the React data manager passes the verification toggle reply (+ objectId / objectType), typed loosely
	handleVerificationToggle(verificationData: object, objectName: string) {
		const verificationInfo = verificationData as VerificationInfo & { spaceAccessId?: number };
		const status = verificationInfo?.status;
		const displayName = `<strong>${objectName}</strong>`;
		if (status === VerificationStatus.VERIFIED) {
			this.infolayerService.ok(`${displayName} has been set as verified dataset.`);
		} else {
			this.infolayerService.info(`Verification has been removed from ${displayName}.`);
		}

		const objectId = String(
			verificationInfo?.objectId ?? verificationInfo?.spaceAccessId ?? '',
		);
		const source = this.mmSourcesService.findModel(objectId);
		source?.directPropUpdate({ verificationInfo });

		this.eventManagerService.trigger(AppEvent.VERIFICATION_CHANGED, {
			objectType: VerificationObjectType.DATASET,
			objectId,
			status,
		});
	}

	@action
	async handleLineageVerify(target: LineageVerifyTarget): Promise<void> {
		if (this.accountService.handleDemo()) {
			return;
		}

		await this.lineageService.verify(target);
	}

	@action
	handleWatchVideo(videoUrl: string) {
		this.navigationService.openVideoPopup(videoUrl, true);
	}

	@action
	async handleViewMetricInsights(metricKey: string) {
		// The metrics store is filled on request rather than eagerly per route; `requestMetrics` is
		// deduplicated, so this is a no-op once the store is warm.
		await this.mmMetricsService.requestMetrics();

		const metric = this.mmMetricsService.findRecord(metricKey);
		if (!metric) {
			this.logToConsole(
				'handleViewMetricInsights - metric not found in store',
				metricKey,
				'similar record ids:',
				this.mmMetricsService.savedRecords
					.map((record) => String(record.id))
					.filter((id) => id.startsWith(metricKey)),
			);
			return;
		}

		const metricSetup = new MetricSetup(metric.metricData!);

		this.metricDetailsService.openMetricDetailsDrawer(
			{
				metricSetup,
			},
			'datasets',
		);
	}

	@action
	handleLineageNavigate(kind: string, id: string, intent?: LineageNavigateIntent) {
		this.lineageService.navigate(kind, id, intent);
	}

	@action
	handleDataSync(source: Source, data: APIData.App.BgEvent) {
		if (
			data.syncType === DataSourceSyncType.INITIAL_SYNC &&
			source.createdById.toString() === this.currentUserService.id &&
			data.selectedSourceOption === SourceOption.DATASETS &&
			data.isSample === false
		) {
			const payload = {
				title: source.title,
				description: source.parentInfo?.title ?? source.subtitle,
				avatar: source.realAvatar,
				spaceAccessId: source.numericId!,
			};

			switch (data.syncStatus) {
				case DataSourceSyncStatus.IN_PROGRESS:
					this.dataSyncInfoLayers[source.numericId!] =
						this.infolayerService.datasetSync(payload);
					break;
				case DataSourceSyncStatus.SUCCESS:
				case DataSourceSyncStatus.SUCCESS_WITH_WARNINGS:
					if (this.dataSyncInfoLayers[source.numericId!]?.visible) {
						this.dataSyncInfoLayers[source.numericId!]?.setDone(payload);
					} else {
						this.dataSyncInfoLayers[source.numericId!] =
							this.infolayerService.datasetSync(payload);
						this.dataSyncInfoLayers[source.numericId!]?.setDone(payload);
					}
					break;
				case DataSourceSyncStatus.FAILED:
					this.dataSyncInfoLayers[source.numericId!]?.setError(data.errorText);
					break;
				default:
			}
		}
	}
}

declare module '@ember/service' {
	interface Registry {
		'dataset-events': DatasetEventsService;
	}
}
